<template>
  <div class=" text-sm flex flex-row items-center justify-start">
    <!-- Checkbox input and custom visual -->
    <div
      class="inline-flex items-center justify-center"
      :class="{
        checked: modelValue,
        disabled: disabled,
      }"
      :disabled="disabled"

      :data-test-id="generateDataTestId(dataTestId, 'checkbox')"
    >
      <input
        :id="checkboxId"
        type="checkbox"
        class="relative rounded-sm disabled:bg-surface-dark disabled:border-surface-dark disabled:pointer-events-none border border-solid  size-5 accent-brand outline-brand bg-surface-light group-hover:border-brand"
        :class="modelValue ? 'border-brand' : 'border-surface-dark appearance-none' "
        :checked="modelValue"
        :disabled="disabled"
        :required="required"
        :aria-label="ariaLabel || 'label' || '' "
        @change="updateValue($event)"
      >
    </div>
    <!-- Label if provided -->
    <label
      v-if="label"
      class="am3-checkbox-label pl-2"
      :class="{
        'text-[var(--font-colour-danger)]': required && !modelValue && validationState !== true,
        'cursor-pointer': !disabled,
        'select-none': disabled,
      }"
      :for="checkboxId"
    >
      {{ label }}
    </label>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import '@/components/am3-generic/core/theme.css'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'
import { useFormValidation } from '@/components/am3-generic/inputs/useFormValidation'

/**
 * AM3Checkbox component
 *
 * A checkbox component with custom styling and animation
 */
export default defineComponent({
  name: 'AM3Checkbox',

  props: {
    // v-model value
    modelValue: {
      type: Boolean,
      default: false,
    },

    // Label text
    label: {
      type: String,
      default: '',
    },

    // Disabled state
    disabled: {
      type: Boolean,
      default: false,
    },

    // Required state
    required: {
      type: Boolean,
      default: false,
    },

    // Data test id prefix
    dataTestId: {
      type: String,
      default: props => props.label,
    },

    // Accessibility label
    ariaLabel: {
      type: String,
      required: false,
    },

    // Custom ID for the checkbox input, so a parent can `<label for>` it directly
    id: {
      type: String,
      default: '',
    },

  },

  emits: ['update:modelValue', 'change'],

  setup(props, { emit }) {
    // Use provided ID or generate a unique one for the checkbox
    const checkboxId = ref(props.id || `am3-checkbox-${uuidv4()}`)

    // Form validation
    const validateFn = () => {
      if (props.required && !props.modelValue) {
        return 'This field is required'
      }
      return true
    }

    const {
      validationState,
      runValidation,
      resetValidation,
    } = useFormValidation(validateFn, props.modelValue, props.required)

    // Reset validation when modelValue changes
    watch(() => props.modelValue, (newValue) => {
      if (newValue) {
        resetValidation()
      }
      else {
        runValidation(newValue, props.required)
      }
    })

    // Update model value
    const updateValue = (event: Event) => {
      if (!props.disabled) {
        const newValue = !props.modelValue
        emit('update:modelValue', newValue)
        emit('change', newValue)

        // Run validation after updating value
        if (props.required && !newValue) {
          runValidation(newValue, props.required)
        }
        else {
          resetValidation()
        }
      }
    }

    // Method for form validation directive to call
    const validateCheckbox = () => {
      return runValidation(props.modelValue, props.required)
    }

    // Expose validate function for external form validation

    return {
      checkboxId,
      updateValue,
      generateDataTestId,
      validationState,
      runValidation,
      resetValidation,
      validate: validateCheckbox,
    }
  },
})
</script>
